import { useInfiniteQuery } from '@tanstack/react-query';
import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';
import type {
  BillingStatementStatus,
  ListBillingStatementsResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed client + React Query hook for ops-api `/v1/ops/billing-statements`
 * (epic #1033 E4, #1956) — the AR statement read surface.
 *
 * Paging is keyset over `(statement_period_start DESC, id DESC)`; the client
 * hands `next_cursor` back verbatim, so `useInfiniteQuery` with
 * `getNextPageParam` is the whole story.
 *
 * `overdue` is a SERVER-SIDE filter, not a client-side date comparison. The
 * BFF answers it by intersecting with the dunning feed, which applies the C5
 * live-debt suppression: an account that has already cleared its debt emits no
 * overdue item even though its `open` statements remain on the page. Anything
 * this module computed from `due_date` would disagree with the reminder the
 * customer actually receives — so it computes nothing.
 *
 * Money arrives as integer micro-USD and stays that way until a formatter
 * renders it.
 */

const BASE_PATH = '/v1/ops/billing-statements';

export interface BillingStatementsQuery {
  readonly accountId?: string;
  readonly status?: BillingStatementStatus;
  readonly overdue?: boolean;
  readonly limit?: number;
}

function buildQueryString(query: BillingStatementsQuery, cursor: string | null): string {
  const params = new URLSearchParams();
  if (typeof query.accountId === 'string' && query.accountId.length > 0) {
    params.set('account_id', query.accountId);
  }
  if (query.status !== undefined) {
    params.set('status', query.status);
  }
  if (query.overdue === true) {
    params.set('overdue', 'true');
  }
  if (typeof query.limit === 'number' && Number.isFinite(query.limit)) {
    params.set('limit', String(query.limit));
  }
  if (cursor !== null && cursor.length > 0) {
    params.set('cursor', cursor);
  }
  const encoded = params.toString();
  return encoded.length > 0 ? `?${encoded}` : '';
}

export async function listBillingStatements(
  query: BillingStatementsQuery,
  cursor: string | null,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListBillingStatementsResponse> {
  return opsApiRequest<ListBillingStatementsResponse>(
    `${BASE_PATH}${buildQueryString(query, cursor)}`,
    { signal },
    fetchImpl,
  );
}

export function billingStatementsQueryKey(
  query: BillingStatementsQuery,
): readonly [
  'billingStatements',
  'list',
  { accountId: string | null; status: string | null; overdue: boolean; limit: number | null },
] {
  return [
    'billingStatements',
    'list',
    {
      accountId: query.accountId ?? null,
      status: query.status ?? null,
      overdue: query.overdue === true,
      limit: query.limit ?? null,
    },
  ] as const;
}

export function useBillingStatements(
  query: BillingStatementsQuery,
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<ListBillingStatementsResponse, string | null>, Error> {
  const { fetchImpl } = useSession();
  return useInfiniteQuery<
    ListBillingStatementsResponse,
    Error,
    InfiniteData<ListBillingStatementsResponse, string | null>,
    ReturnType<typeof billingStatementsQueryKey>,
    string | null
  >({
    queryKey: billingStatementsQueryKey(query),
    queryFn: ({ pageParam, signal }) =>
      listBillingStatements(query, pageParam ?? null, fetchImpl, signal),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    enabled,
    retry: false,
  });
}
